/**
 * @module FleetAdminPanel
 * @description Admin UI for fleet lane management — add, edit, and delete lanes.
 * Reads all lanes from the parent FleetManager and provides CRUD forms + paginated table.
 * Edit and Delete are blocked for lanes currently in use (status === 'IN_USE').
 * Form fields use datalist dropdowns (with clear buttons) for fields with known values,
 * while still allowing free-text input for new values. Peripherals use checkboxes.
 * Changes write to DynamoDB via POST/PUT/DELETE /fleet/lanes API endpoints.
 */
import React, { useState, useMemo } from 'react';
import { Plus, Pencil, Trash2, X, Save, Search } from 'lucide-react';
import { laneWebUiUrl } from './fleetConstants';

const LANE_TYPES = ['POS', 'SCO', 'ISS'];

const EMPTY_FORM = {
  laneId: '', hardwareId: '', laneType: '', deviceId: '', ipAddress: '', port: '',
  dsn: '', peripherals: '', webUiUrl: '', lab: '', storeId: '', storeLabel: '',
  deploymentStage: '', enterpriseConfig: '', enterpriseUrl: '', adminUiUrl: '', gammaLibby: '',
};

function FleetAdminPanel({ lanes, onCreateLane, onUpdateLane, onDeleteLane, actionLoading }) {
  const [mode, setMode] = useState(null); // 'add' | 'edit' | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingLaneId, setEditingLaneId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const PAGE_SIZE_OPTIONS = [10, 15, 25, 50];

  const filteredLanes = useMemo(() => {
    if (!searchFilter.trim()) return lanes;
    const q = searchFilter.toLowerCase();
    return lanes.filter(l =>
      l.laneId.toLowerCase().includes(q) ||
      l.hardwareId?.toLowerCase().includes(q) ||
      l.deviceId?.toLowerCase().includes(q) ||
      l.ipAddress?.toLowerCase().includes(q) ||
      l.dsn?.toLowerCase().includes(q) ||
      l.storeId?.toLowerCase().includes(q) ||
      l.deploymentStage?.toLowerCase().includes(q) ||
      l.laneType?.toLowerCase().includes(q) ||
      l.status?.toLowerCase().includes(q)
    );
  }, [lanes, searchFilter]);

  const totalPages = Math.ceil(filteredLanes.length / pageSize) || 1;
  const safePage = Math.min(currentPage, totalPages);
  const paginatedLanes = filteredLanes.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Delegates to the shared builder (fleetConstants.laneWebUiUrl) so the admin
  // form writes the exact same URL shape the Fleet render sites derive.
  const generateWebUiUrl = (ip, port, deviceId, laneType) =>
    laneWebUiUrl({ ipAddress: ip, port, deviceId, laneType });

  const handleFieldChange = (field, value) => {
    const updated = { ...form, [field]: value };
    if (field === 'laneType') {
      const defaultPort = value === 'ISS' ? '8080' : '8083';
      if (!form.port || form.port === '8080' || form.port === '8083') {
        updated.port = defaultPort;
      }
    }
    if (['ipAddress', 'port', 'deviceId', 'laneType'].includes(field)) {
      updated.webUiUrl = generateWebUiUrl(
        field === 'ipAddress' ? value : updated.ipAddress,
        field === 'port' ? value : updated.port,
        field === 'deviceId' ? value : updated.deviceId,
        field === 'laneType' ? value : updated.laneType
      );
    }
    setForm(updated);
  };

  const handleAdd = () => {
    setMode('add');
    setForm(EMPTY_FORM);
    setEditingLaneId(null);
    setError('');
    setSuccess('');
  };

  const handleEdit = (lane) => {
    setMode('edit');
    setEditingLaneId(lane.laneId);
    setForm({
      laneId: lane.laneId || '',
      hardwareId: lane.hardwareId || '',
      laneType: lane.laneType || 'POS',
      deviceId: lane.deviceId || '',
      ipAddress: lane.ipAddress || '',
      port: lane.port || '8083',
      dsn: lane.dsn || '',
      peripherals: lane.peripherals || '',
      webUiUrl: lane.webUiUrl || '',
      lab: lane.lab || '',
      storeId: lane.storeId || '',
      storeLabel: lane.storeLabel || '',
      deploymentStage: lane.deploymentStage || '',
      enterpriseConfig: lane.enterpriseConfig || '',
      enterpriseUrl: lane.enterpriseUrl || '',
      adminUiUrl: lane.adminUiUrl || '',
      gammaLibby: lane.gammaLibby || '',
    });
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setMode(null);
    setForm(EMPTY_FORM);
    setEditingLaneId(null);
    setError('');
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!form.laneId.trim() || !form.hardwareId.trim() || !form.laneType || !form.deviceId.trim() || !form.ipAddress.trim() || !form.port.trim() || !form.deploymentStage.trim() || !form.storeId.trim()) {
      setError('Required fields: Lane ID, Hardware ID, Type, Device ID, IP Address, Port, Deployment Stage, Store ID');
      return;
    }

    if (!/^[a-zA-Z0-9\-_]+$/.test(form.laneId.trim())) {
      setError('Lane ID can only contain letters, numbers, hyphens, and underscores (no spaces or special characters)');
      return;
    }

    if (!/^[a-zA-Z0-9\-_]+$/.test(form.hardwareId.trim())) {
      setError('Hardware ID can only contain letters, numbers, hyphens, and underscores');
      return;
    }

    if (form.hardwareId.trim().length > 15) {
      setError('Hardware ID must be 15 characters or fewer');
      return;
    }

    if (!/^[a-zA-Z0-9\-_\s]+$/.test(form.deviceId.trim())) {
      setError('Device ID can only contain letters, numbers, hyphens, underscores, and spaces');
      return;
    }

    if (!/^(\d{1,3}\.){3}\d{1,3}$/.test(form.ipAddress.trim())) {
      setError('IP Address must be a valid IPv4 format (e.g. 10.240.1.95)');
      return;
    }

    if (form.enterpriseUrl.trim() && !/^https?:\/\/.+/.test(form.enterpriseUrl.trim())) {
      setError('Enterprise URL must start with http:// or https://');
      return;
    }

    if (form.adminUiUrl.trim() && !/^https?:\/\/.+/.test(form.adminUiUrl.trim())) {
      setError('Admin UI URL must start with http:// or https://');
      return;
    }

    if (form.dsn.trim() && !/^[a-zA-Z0-9\-]+$/.test(form.dsn.trim())) {
      setError('DSN can only contain letters, numbers, and hyphens (no spaces or special characters)');
      return;
    }

    if (form.dsn.trim() && form.dsn.trim().length > 30) {
      setError('DSN must be 30 characters or fewer');
      return;
    }

    if (form.storeId.trim() && !/^[a-zA-Z0-9\-_ ]{1,20}$/.test(form.storeId.trim())) {
      setError('Store ID can contain letters, numbers, spaces, hyphens and underscores (max 20 chars), e.g. 10252 or US-MA-21');
      return;
    }

    const portNum = parseInt(form.port.trim(), 10);
    if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
      setError('Port must be a number between 1 and 65535 (e.g. 8083)');
      return;
    }

    // Prepare form for submission
    const submitForm = { ...form };

    try {
      if (mode === 'add') {
        await onCreateLane(submitForm);
        setSuccess(`Lane ${submitForm.laneId} created successfully`);
      } else {
        await onUpdateLane(editingLaneId, submitForm);
        setSuccess(`Lane ${editingLaneId} updated successfully`);
      }
      setMode(null);
      setForm(EMPTY_FORM);
      setEditingLaneId(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDelete = async (laneId) => {
    setError('');
    setSuccess('');
    try {
      await onDeleteLane(laneId);
      setSuccess(`Lane ${laneId} deleted`);
      setDeleteConfirm(null);
    } catch (e) {
      setError(e.message);
      setDeleteConfirm(null);
    }
  };

  return (
    <div className="fleet-admin-panel">
      <div className="fleet-admin-header">
        <h3>Manage Fleet Lanes</h3>
        <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={mode === 'add'}>
          <Plus size={14} /> Add Lane
        </button>
      </div>
      <p className="fleet-admin-tip"><span className="fleet-admin-tip-icon">💡</span> The <strong>Deployment Stage</strong> field determines which tab a lane appears under. A new stage value automatically creates a new tab. Lanes are grouped by <strong>Store ID</strong>.</p>

      {error && <div className="fleet-error">{error} <button onClick={() => setError('')}>×</button></div>}
      {success && <div className="fleet-success">{success} <button onClick={() => setSuccess('')}>×</button></div>}

      {mode && (
        <div className="fleet-admin-form">
          <div className="fleet-admin-form-header">
            <h4>{mode === 'add' ? 'Add New Lane' : `Edit Lane: ${editingLaneId}`}</h4>
            <button className="btn btn-secondary btn-sm" onClick={handleCancel}><X size={14} /></button>
          </div>
          <div className="fleet-admin-form-grid">
            <div className="form-group">
              <label>Lane ID *</label>
              <input value={form.laneId} onChange={e => handleFieldChange('laneId', e.target.value)} disabled={mode === 'edit'} placeholder="e.g. APX019" maxLength={15} />
            </div>
            <div className="form-group">
              <label>Hardware ID *</label>
              <input value={form.hardwareId} onChange={e => handleFieldChange('hardwareId', e.target.value)} placeholder="e.g. APX019" />
            </div>
            <div className="form-group">
              <label>Lane Type *</label>
              <div className="fleet-input-clearable">
                <input
                  list="lane-type-options"
                  value={form.laneType}
                  onChange={e => handleFieldChange('laneType', e.target.value)}
                  placeholder="Select or type a new type"
                />
                {form.laneType && <button type="button" className="fleet-input-clear" onClick={() => handleFieldChange('laneType', '')}>✕</button>}
              </div>
              <datalist id="lane-type-options">
                {LANE_TYPES.map(t => <option key={t} value={t} />)}
              </datalist>
            </div>
            <div className="form-group">
              <label>Device ID *</label>
              <input value={form.deviceId} onChange={e => handleFieldChange('deviceId', e.target.value)} placeholder={form.laneType === 'ISS' ? 'e.g. ISS 10438' : form.laneType === 'SCO' ? 'e.g. NPEWFM10252SCO101' : 'e.g. NPEWFM10252REG007'} />
            </div>
            <div className="form-group">
              <label>IP Address *</label>
              <input value={form.ipAddress} onChange={e => handleFieldChange('ipAddress', e.target.value)} placeholder="e.g. 10.240.1.110" />
            </div>
            <div className="form-group">
              <label>Port *</label>
              <div className="fleet-input-clearable">
                <input
                  list="port-options"
                  value={form.port}
                  onChange={e => handleFieldChange('port', e.target.value)}
                  placeholder={form.laneType === 'ISS' ? 'e.g. 8080' : 'e.g. 8083'}
                />
                {form.port && <button type="button" className="fleet-input-clear" onClick={() => handleFieldChange('port', '')}>✕</button>}
              </div>
              <datalist id="port-options">
                {[...new Set(lanes.map(l => l.port).filter(Boolean))].sort().map(val => (
                  <option key={val} value={val} />
                ))}
              </datalist>
            </div>
            <div className="form-group">
              <label>DSN</label>
              <input value={form.dsn} onChange={e => handleFieldChange('dsn', e.target.value)} placeholder="e.g. 242ffa3c1234" />
            </div>
            <div className="form-group">
              <label>Peripherals</label>
              <div className="fleet-peripherals-checkboxes">
                {['Scanner', 'Scale', 'Printer', 'Cash Drawer', 'Pinpad'].map(item => {
                  const selected = (form.peripherals || '').split(',').map(s => s.trim()).filter(Boolean);
                  const isChecked = selected.includes(item);
                  return (
                    <label key={item} className="fleet-checkbox-label">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          const updated = isChecked
                            ? selected.filter(s => s !== item)
                            : [...selected, item];
                          handleFieldChange('peripherals', updated.join(', '));
                        }}
                      />
                      {item}
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="form-group">
              <label>Deployment Stage *</label>
              <div className="fleet-input-clearable">
                <input
                  list="deployment-stage-options"
                  value={form.deploymentStage}
                  onChange={e => handleFieldChange('deploymentStage', e.target.value)}
                  placeholder="Select or type a new stage"
                />
                {form.deploymentStage && <button type="button" className="fleet-input-clear" onClick={() => handleFieldChange('deploymentStage', '')}>✕</button>}
              </div>
              <datalist id="deployment-stage-options">
                <option value="Shared" />
                <option value="Hawkeye" />
                <option value="Beta" />
                <option value="Gamma" />
              </datalist>
            </div>
            <div className="form-group">
              <label>Store ID *</label>
              <div className="fleet-input-clearable">
                <input
                  list="store-id-options"
                  value={form.storeId}
                  onChange={e => handleFieldChange('storeId', e.target.value)}
                  placeholder="Select or type a new Store ID"
                />
                {form.storeId && <button type="button" className="fleet-input-clear" onClick={() => handleFieldChange('storeId', '')}>✕</button>}
              </div>
              <datalist id="store-id-options">
                {[...new Set(lanes.map(l => l.storeId).filter(Boolean))].sort().map(id => (
                  <option key={id} value={id} />
                ))}
              </datalist>
            </div>
            <div className="form-group">
              <label>Lab</label>
              <div className="fleet-input-clearable">
                <input
                  list="lab-options"
                  value={form.lab}
                  onChange={e => handleFieldChange('lab', e.target.value)}
                  placeholder="Select or type a new lab"
                />
                {form.lab && <button type="button" className="fleet-input-clear" onClick={() => handleFieldChange('lab', '')}>✕</button>}
              </div>
              <datalist id="lab-options">
                {[...new Set(lanes.map(l => l.lab).filter(Boolean))].sort().map(val => (
                  <option key={val} value={val} />
                ))}
              </datalist>
            </div>
            <div className="form-group form-group-wide">
              <label>Store Label</label>
              <div className="fleet-input-clearable">
                <input
                  list="store-label-options"
                  value={form.storeLabel}
                  onChange={e => handleFieldChange('storeLabel', e.target.value)}
                  placeholder="Select or type a new label"
                />
                {form.storeLabel && (
                  <button type="button" className="fleet-input-clear" onClick={() => handleFieldChange('storeLabel', '')}>✕</button>
                )}
              </div>
              <div className="fleet-form-hint">e.g. Store 10252 — Hawkeye (Austin, TX)</div>
              <datalist id="store-label-options">
                {[...new Set(lanes.map(l => l.storeLabel).filter(Boolean))].sort().map(val => (
                  <option key={val} value={val} />
                ))}
              </datalist>
            </div>
            <div className="form-group">
              <label>Enterprise Config</label>
              <div className="fleet-input-clearable">
                <input
                  list="enterprise-config-options"
                  value={form.enterpriseConfig}
                  onChange={e => handleFieldChange('enterpriseConfig', e.target.value)}
                  placeholder="Select or type a new config"
                />
                {form.enterpriseConfig && <button type="button" className="fleet-input-clear" onClick={() => handleFieldChange('enterpriseConfig', '')}>✕</button>}
              </div>
              <datalist id="enterprise-config-options">
                {[...new Set(lanes.map(l => l.enterpriseConfig).filter(Boolean))].sort().map(val => (
                  <option key={val} value={val} />
                ))}
              </datalist>
            </div>
            <div className="form-group">
              <label>Enterprise URL</label>
              <input value={form.enterpriseUrl} onChange={e => handleFieldChange('enterpriseUrl', e.target.value)} placeholder="e.g. http://elera-enterprise-..." />
            </div>
            <div className="form-group">
              <label>Admin UI URL</label>
              <input value={form.adminUiUrl} onChange={e => handleFieldChange('adminUiUrl', e.target.value)} placeholder="e.g. https://beta.abkansal.people.amazon.dev/authorization-admin-ui/#/login" />
            </div>
            <div className="form-group">
              <label>Gamma Libby</label>
              <div className="fleet-input-clearable">
                <input
                  list="gamma-libby-options"
                  value={form.gammaLibby}
                  onChange={e => handleFieldChange('gammaLibby', e.target.value)}
                  placeholder="Select or type a new Libby ID"
                />
                {form.gammaLibby && <button type="button" className="fleet-input-clear" onClick={() => handleFieldChange('gammaLibby', '')}>✕</button>}
              </div>
              <datalist id="gamma-libby-options">
                {[...new Set(lanes.map(l => l.gammaLibby).filter(Boolean))].sort().map(val => (
                  <option key={val} value={val} />
                ))}
              </datalist>
            </div>
            <div className="form-group form-group-full">
              <label>Web UI URL (auto-generated)</label>
              <input value={form.webUiUrl} onChange={e => handleFieldChange('webUiUrl', e.target.value)} placeholder="Auto-generated from IP + port + deviceId" />
            </div>
          </div>
          <div className="fleet-admin-form-actions">
            <button className="btn btn-primary" onClick={handleSubmit} disabled={actionLoading}>
              <Save size={14} /> {actionLoading ? 'Saving...' : (mode === 'add' ? 'Create Lane' : 'Update Lane')}
            </button>
            <button className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
          </div>
        </div>
      )}

      <div className="fleet-admin-search">
        <Search size={14} />
        <input
          type="text"
          placeholder="Filter by Lane ID, Hardware ID, Type, Device ID, IP, DSN, Store, Stage, Status..."
          value={searchFilter}
          onChange={e => { setSearchFilter(e.target.value); setCurrentPage(1); }}
        />
        {searchFilter && (
          <button className="fleet-admin-search-clear" onClick={() => { setSearchFilter(''); setCurrentPage(1); }}>Clear</button>
        )}
      </div>

      <div className="fleet-admin-table-wrapper">
        <table className="fleet-admin-table">
          <thead>
            <tr>
              <th>Lane ID</th>
              <th>Hardware ID</th>
              <th>Type</th>
              <th>Device ID</th>
              <th>IP:Port</th>
              <th>DSN</th>
              <th>Store</th>
              <th>Stage</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedLanes.map(lane => (
              <tr key={lane.laneId} className={lane.status === 'IN_USE' ? 'row-busy' : ''}>
                <td><strong>{lane.laneId}</strong></td>
                <td>{lane.hardwareId}</td>
                <td>{lane.laneType}</td>
                <td className="td-mono">{lane.deviceId}</td>
                <td className="td-mono">{lane.ipAddress}:{lane.port}</td>
                <td className="td-mono">{lane.dsn || '—'}</td>
                <td>{lane.storeId}</td>
                <td>{lane.deploymentStage}</td>
                <td>
                  <span className={`admin-status ${lane.status === 'FREE' ? 'admin-status-free' : 'admin-status-busy'}`}>
                    {lane.status === 'FREE' ? 'Free' : `In use (${lane.allocatedBy})`}
                  </span>
                </td>
                <td className="td-actions">
                  <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(lane)} title="Edit" disabled={lane.status === 'IN_USE'}>
                    <Pencil size={12} />
                  </button>
                  {deleteConfirm === lane.laneId ? (
                    <span className="delete-confirm">
                      <span>Delete?</span>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(lane.laneId)} disabled={actionLoading}>Yes</button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setDeleteConfirm(null)}>No</button>
                    </span>
                  ) : (
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => setDeleteConfirm(lane.laneId)}
                      title="Delete"
                      disabled={lane.status === 'IN_USE'}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredLanes.length === 0 && <div className="fleet-admin-empty">No lanes found</div>}
        {filteredLanes.length > 0 && (
          <div className="fleet-admin-pagination">
            <div className="fleet-admin-page-size">
              <span>Show</span>
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>per page</span>
            </div>
            <div className="fleet-admin-page-nav">
              <button className="btn btn-sm btn-secondary" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1}>← Prev</button>
              <span className="fleet-admin-page-info">Page {safePage} of {totalPages}</span>
              <button className="btn btn-sm btn-secondary" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}>Next →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FleetAdminPanel;
